import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import { Container, Row, Col, Card, Table, Badge, Form, InputGroup, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { AuthorizationCheck } from '../../utils/AuthorizationCheck';

const DashboardHistory = ({ auth }) => {
  const [articles, setArticles] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tenantInfo, setTenantInfo] = useState(null);
  const [canSeeAllHistory, setCanSeeAllHistory] = useState(false);

  const [filters, setFilters] = useState({
    search: '',
    status: '',
    limit: 25,
    offset: 0
  });

  // Fetch tenant info on mount
  useEffect(() => {
    if (auth && auth.id) {
      fetchTenantInfo();
    }
  }, [auth]);

  useEffect(() => {
    if (auth && auth.id) {
      fetchHistory();
    }
  }, [auth, filters.status, filters.offset]);

  const fetchTenantInfo = async () => {
    try {
      const response = await fetch('/api/user/tenants', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        const activeTenant = data.tenants?.find(t => t.is_active) || data.tenants?.[0];
        setTenantInfo(activeTenant);
        // Admins and managers can see all history
        const isAdminOrManager = activeTenant?.role === 'admin' || activeTenant?.role === 'manager';
        setCanSeeAllHistory(isAdminOrManager);
      }
    } catch (err) {
      console.error('Error fetching tenant info:', err);
    }
  };

  const fetchHistory = async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      params.append('limit', filters.limit);
      params.append('offset', filters.offset);

      const response = await fetch(`/api/user/history?${params}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setArticles(data.articles || []);
        setTotal(data.total || 0);
      } else {
        setError('Failed to fetch history');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString();
  };

  const formatNumber = (num) => (num || 0).toLocaleString();

  const getStatusBadge = (status) => {
    const variants = {
      completed: 'success',
      processing: 'warning',
      failed: 'danger',
      pending: 'secondary'
    };
    return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
  };

  const filteredArticles = articles.filter(article => {
    if (!filters.search) return true;
    const search = filters.search.toLowerCase();
    return (
      (article.article_title || '').toLowerCase().includes(search) ||
      (article.source_url || '').toLowerCase().includes(search) ||
      (article.source_domain || '').toLowerCase().includes(search)
    );
  });

  const totalPages = Math.ceil(total / filters.limit);
  const currentPage = Math.floor(filters.offset / filters.limit) + 1;

  // Check authorization
  const renderPage = AuthorizationCheck(auth);
  if (!renderPage) {
    return null;
  }

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>{canSeeAllHistory ? 'Organization History' : 'My History'} - Spawnry</title>
      </Helmet>

      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        padding: '100px 0 3rem'
      }}>
        <Container>
          <Row>
            <Col>
              <div className="d-flex align-items-center mb-2">
                <Link to="/dashboard" style={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none', marginRight: '0.5rem' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M19 12H5M12 19l-7-7 7-7"/>
                  </svg>
                </Link>
                <h1 style={{
                  color: 'white',
                  fontWeight: '700',
                  fontSize: 'clamp(1.5rem, 3vw, 2rem)',
                  margin: 0
                }}>
                  {canSeeAllHistory ? 'Organization History' : 'My Article History'}
                </h1>
              </div>
              <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '1rem', margin: 0 }}>
                {canSeeAllHistory
                  ? `View all articles generated by ${tenantInfo?.name || 'your organization'}`
                  : 'View all articles you\'ve generated'}
              </p>
            </Col>
          </Row>
        </Container>
      </div>

      <Container style={{ marginTop: '-1.5rem', paddingBottom: '3rem' }}>
        {/* Filters */}
        <Card className="mb-4" style={{ border: 'none', borderRadius: '16px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
          <Card.Body>
            <Row className="align-items-center">
              <Col md={5}>
                <InputGroup>
                  <InputGroup.Text style={{ background: '#f8fafc', border: 'none' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2">
                      <circle cx="11" cy="11" r="8"/>
                      <path d="m21 21-4.35-4.35"/>
                    </svg>
                  </InputGroup.Text>
                  <Form.Control
                    placeholder="Search articles..."
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    style={{ border: 'none', background: '#f8fafc' }}
                  />
                </InputGroup>
              </Col>
              <Col md={4}>
                <Form.Select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value, offset: 0 })}
                  style={{ border: 'none', background: '#f8fafc' }}
                >
                  <option value="">All Status</option>
                  <option value="completed">Completed</option>
                  <option value="processing">Processing</option>
                  <option value="failed">Failed</option>
                </Form.Select>
              </Col>
              <Col md={3} className="text-end">
                <span style={{ color: '#64748b', fontSize: '0.9rem' }}>
                  {formatNumber(total)} articles
                </span>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Articles Table */}
        <Card style={{ border: 'none', borderRadius: '16px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
          <Card.Body className="p-0">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-secondary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2 text-muted">Loading history...</p>
              </div>
            ) : error ? (
              <div className="text-center py-5 text-danger">
                <p>{error}</p>
              </div>
            ) : (
              <Table responsive hover className="mb-0">
                <thead style={{ background: '#f8fafc' }}>
                  <tr>
                    <th style={{ padding: '1rem', fontWeight: '600', color: '#64748b', fontSize: '0.85rem' }}>Article</th>
                    {canSeeAllHistory && (
                      <th style={{ padding: '1rem', fontWeight: '600', color: '#64748b', fontSize: '0.85rem' }}>Created By</th>
                    )}
                    <th style={{ padding: '1rem', fontWeight: '600', color: '#64748b', fontSize: '0.85rem' }}>Status</th>
                    <th style={{ padding: '1rem', fontWeight: '600', color: '#64748b', fontSize: '0.85rem' }}>Tokens</th>
                    <th style={{ padding: '1rem', fontWeight: '600', color: '#64748b', fontSize: '0.85rem' }}>Date</th>
                    <th style={{ padding: '1rem', fontWeight: '600', color: '#64748b', fontSize: '0.85rem' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredArticles.length === 0 ? (
                    <tr>
                      <td colSpan={canSeeAllHistory ? 6 : 5} className="text-center py-5 text-muted">
                        {filters.search || filters.status ? 'No articles match your filters' : 'No articles yet'}
                        {!filters.search && !filters.status && (
                          <div className="mt-2">
                            <Link to="/app/article-to-media" className="btn btn-primary btn-sm">
                              Create your first article
                            </Link>
                          </div>
                        )}
                      </td>
                    </tr>
                  ) : (
                    filteredArticles.map((article, idx) => (
                      <tr key={idx}>
                        <td style={{ padding: '1rem' }}>
                          <div>
                            <div style={{
                              maxWidth: '300px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              fontWeight: '500',
                              color: '#1e293b'
                            }}>
                              {article.article_title || 'Untitled'}
                            </div>
                            <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                              {article.source_domain}
                            </div>
                          </div>
                        </td>
                        {canSeeAllHistory && (
                          <td style={{ padding: '1rem', color: '#64748b', fontSize: '0.9rem' }}>
                            {article.created_by ? (
                              <span title={article.created_by.email}>
                                {article.created_by.name}
                              </span>
                            ) : (
                              <span style={{ color: '#94a3b8' }}>—</span>
                            )}
                          </td>
                        )}
                        <td style={{ padding: '1rem' }}>
                          {getStatusBadge(article.status)}
                        </td>
                        <td style={{ padding: '1rem', color: '#64748b' }}>
                          {formatNumber(article.total_tokens_charged)}
                        </td>
                        <td style={{ padding: '1rem', color: '#64748b', fontSize: '0.9rem' }}>
                          {formatDate(article.created_at)}
                        </td>
                        <td style={{ padding: '1rem' }}>
                          {article.status === 'completed' && article.slug && (
                            <Link
                              to={`/app/article-to-media/${article.slug}`}
                              className="btn btn-outline-primary btn-sm"
                              target="_blank"
                            >
                              View
                            </Link>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            )}
          </Card.Body>

          {/* Pagination */}
          {totalPages > 1 && (
            <Card.Footer className="d-flex justify-content-between align-items-center" style={{ background: '#fafafa', borderTop: '1px solid #e2e8f0' }}>
              <span style={{ color: '#64748b', fontSize: '0.9rem' }}>
                Showing {filters.offset + 1} - {Math.min(filters.offset + filters.limit, total)} of {total}
              </span>
              <div className="d-flex gap-2">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => setFilters({ ...filters, offset: filters.offset - filters.limit })}
                >
                  Previous
                </Button>
                <span className="align-self-center" style={{ color: '#64748b', fontSize: '0.9rem' }}>
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={currentPage === totalPages}
                  onClick={() => setFilters({ ...filters, offset: filters.offset + filters.limit })}
                >
                  Next
                </Button>
              </div>
            </Card.Footer>
          )}
        </Card>
      </Container>
    </>
  );
};

function mapStateToProps({ auth }) {
  return { auth };
}

export default connect(mapStateToProps)(DashboardHistory);
